import { BUSINESS_TIMEZONE } from '@omecare/utils';
import { fr } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';
import { Check, Loader2 } from 'lucide-react';
import { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  MoveAgendaSlotModalApi,
  useMoveAgendaSlotModalViewModel,
} from './move-agenda-slot-modal.viewmodel';

export type MoveAgendaSlotModalProps = {
  show?: boolean;
} & MoveAgendaSlotModalApi;

export const MoveAgendaSlotModal = forwardRef(
  (
    {
      show = true,
      sourceSlotId,
      onSuccess,
      onClose,
      onCancel,
    }: MoveAgendaSlotModalProps,
    ref,
  ) => {
    const { t } = useTranslation();

    const viewModel = useMoveAgendaSlotModalViewModel({
      sourceSlotId,
      onSuccess,
      onClose,
      onCancel,
    });

    useImperativeHandle(ref, () => ({
      close: viewModel.api.close,
    }));

    const formatSlotTime = (date: Date) => {
      return formatInTimeZone(date, BUSINESS_TIMEZONE, 'HH:mm', { locale: fr });
    };

    const formatSlotDate = (date: Date) => {
      return formatInTimeZone(date, BUSINESS_TIMEZONE, 'dd/MM/yyyy', {
        locale: fr,
      });
    };

    const getPeriodLabel = (period: string) => {
      return t(`move-agenda-slot-modal.period.${period}`, period);
    };

    const getSlotTypeLabel = (type: string) => {
      return t(`move-agenda-slot-modal.slot-type.${type}`, type);
    };

    const getAssistantLabel = (withAssistant: boolean) => {
      return withAssistant
        ? t('agenda-slot.retrocession-rate.with_assistant')
        : t('agenda-slot.retrocession-rate.without_assistant');
    };

    return (
      <Dialog
        open={show}
        onOpenChange={(open) => {
          if (!open) viewModel.api.close();
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('move-agenda-slot-modal.title')}</DialogTitle>
            <DialogDescription>
              {t('move-agenda-slot-modal.description')}
            </DialogDescription>
          </DialogHeader>

          <div>
            {viewModel.data.isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {viewModel.data.isError && (
              <div className="text-destructive text-sm">
                {t('move-agenda-slot-modal.error-loading')}
              </div>
            )}

            {!viewModel.data.isLoading &&
              !viewModel.data.isError &&
              !viewModel.data.hasAvailableSlots && (
                <div className="text-muted-foreground text-sm">
                  {t('move-agenda-slot-modal.no-slots-available')}
                </div>
              )}

            {!viewModel.data.isLoading &&
              !viewModel.data.isError &&
              viewModel.data.hasAvailableSlots && (
                <>
                  <div className="mb-2 text-muted-foreground text-sm">
                    {t('move-agenda-slot-modal.available-slots-count', {
                      count:
                        viewModel.data.moveOptions?.availableSlots.length ?? 0,
                    })}
                  </div>

                  <div className="max-h-96 space-y-2 overflow-y-auto">
                    {viewModel.data.moveOptions?.availableSlots.map((slot) => {
                      const isSelected =
                        viewModel.data.selectedSlotId === slot.id;
                      return (
                        <Card
                          key={slot.id}
                          className={`cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-primary bg-primary/5'
                              : 'hover:border-input'
                          }`}
                          onClick={() => viewModel.api.selectSlot(slot.id)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start space-x-3">
                              <div className="mt-0.5">
                                {isSelected ? (
                                  <Check className="h-5 w-5 text-primary" />
                                ) : (
                                  <div className="h-5 w-5 rounded-full border-2 border-input" />
                                )}
                              </div>
                              <div className="grid flex-1 grid-cols-2 gap-2">
                                <div>
                                  <div className="font-medium">
                                    {formatSlotTime(slot.from)} -{' '}
                                    {formatSlotTime(slot.to)}
                                  </div>
                                  <div className="text-muted-foreground text-sm">
                                    {formatSlotDate(slot.from)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-muted-foreground text-sm">
                                    {slot.officeName}
                                  </div>
                                  <div className="text-muted-foreground text-sm">
                                    {slot.doctorAttendingName}
                                  </div>
                                </div>
                                <div className="col-span-2">
                                  <div className="flex gap-2 text-xs">
                                    <span className="rounded bg-muted px-2 py-1">
                                      {getPeriodLabel(slot.periodOfTheDay)}
                                    </span>
                                    <span className="rounded bg-muted px-2 py-1">
                                      {getSlotTypeLabel(slot.type)}
                                    </span>
                                    <span className="rounded bg-muted px-2 py-1">
                                      {getAssistantLabel(slot.withAssistant)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={viewModel.api.cancel}
              disabled={viewModel.data.isMoving}
            >
              {t('btn.cancel')}
            </Button>

            <Button
              onClick={viewModel.api.confirm}
              disabled={
                !viewModel.data.selectedSlotId ||
                viewModel.data.isMoving ||
                viewModel.data.isLoading
              }
              loading={viewModel.data.isMoving}
            >
              {t('move-agenda-slot-modal.confirm-button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);
